// setTableOptions — DdlEdit builder + commit dispatcher for the engine-
// specific table options flow surfaced from `StorageTab` and the inline
// DDL toolbar (DSE2 §3.E3 — `alter-table-options`).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice E3 — Engine-specific table options UI.
//
// The Storage tab's "Engine Options" affordance opens a popover with a
// per-engine subset of the cross-engine `TableOptionsChange` shape. The
// popover dispatches DSE2's `alter-table-options` op against the parked
// table's qualified name.
//
// Per-engine surface (mirrors DSE2's `synthesizeAlterTableOptions`):
//   - mssql:  DATA_COMPRESSION (NONE/ROW/PAGE), DURABILITY
//             (SCHEMA_AND_DATA/SCHEMA_ONLY), SYSTEM_VERSIONING (ON/OFF +
//             optional HISTORY_TABLE).
//   - pgsql:  SET (k = v, …) entries + RESET (k, …) entries — verbatim
//             text for both lists.
//   - mysql:  ENGINE, DEFAULT CHARSET, COLLATE, ROW_FORMAT, KEY_BLOCK_SIZE,
//             STATS_PERSISTENT.
//   - oracle: PCTFREE, PCTUSED, INITRANS, MAXTRANS, STORAGE (opaque),
//             LOGGING/NOLOGGING, COMPRESS clause (opaque).
//   - sqlite: rejects — WITHOUT ROWID and STRICT are CREATE TABLE only and
//             cannot be ALTERed; the popover renders a disabled affordance
//             with the documented redirect to the recreate path (Slice H3).

import type { QualifiedName, SqlEngine } from '../../../../sql-grammar/model';
import type {
  DdlEdit,
  TableOptionsChange,
} from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export interface SetTableOptionsArgs {
  readonly table: QualifiedName;
  readonly change: TableOptionsChange;
}

export function buildSetTableOptionsEdit(args: SetTableOptionsArgs): DdlEdit {
  return { op: 'alter-table-options', table: args.table, change: args.change };
}

export async function commitSetTableOptions(
  ctx: VqeDdlEditContext,
  args: SetTableOptionsArgs,
): Promise<void> {
  const edit = buildSetTableOptionsEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

const SET_TABLE_OPTIONS_SUPPORTED: ReadonlySet<SqlEngine> = new Set([
  'mssql', 'postgres', 'mysql', 'oracle',
] as const);

export function isSetTableOptionsSupported(engine: SqlEngine): boolean {
  return SET_TABLE_OPTIONS_SUPPORTED.has(engine);
}

export function getSetTableOptionsUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'sqlite') {
    return (
      'SQLite WITHOUT ROWID and STRICT are CREATE TABLE only and cannot be '
      + 'ALTERed. Recreate the table — copy rows into a fresh table declared '
      + 'with the new attributes, then swap.'
    );
  }
  return '';
}

function isPositiveInteger(value: number): boolean {
  return Number.isInteger(value) && value > 0;
}

function hasMssqlField(c: TableOptionsChange): boolean {
  return (
    c.mssqlDataCompression !== undefined
    || c.mssqlDurability !== undefined
    || c.mssqlSystemVersioning !== undefined
  );
}

function hasPgsqlField(c: TableOptionsChange): boolean {
  return c.pgsqlSetOptions !== undefined || c.pgsqlResetOptions !== undefined;
}

function hasMysqlField(c: TableOptionsChange): boolean {
  return (
    c.mysqlEngine !== undefined
    || c.mysqlDefaultCharset !== undefined
    || c.mysqlCollate !== undefined
    || c.mysqlRowFormat !== undefined
    || c.mysqlKeyBlockSize !== undefined
    || c.mysqlStatsPersistent !== undefined
  );
}

function hasOracleField(c: TableOptionsChange): boolean {
  return (
    c.oraclePctfree !== undefined
    || c.oraclePctused !== undefined
    || c.oracleInitrans !== undefined
    || c.oracleMaxtrans !== undefined
    || c.oracleStorageText !== undefined
    || c.oracleLogging !== undefined
    || c.oracleCompressText !== undefined
  );
}

function hasAnyField(c: TableOptionsChange): boolean {
  return (
    hasMssqlField(c) || hasPgsqlField(c) || hasMysqlField(c) || hasOracleField(c)
  );
}

/** Pre-flight validator. Returns null when args are acceptable, else a
 *  user-facing message. */
export function validateSetTableOptionsForEngine(
  engine: SqlEngine,
  args: SetTableOptionsArgs,
): string | null {
  if (!isSetTableOptionsSupported(engine)) {
    return getSetTableOptionsUnsupportedTooltip(engine);
  }
  if (args.table.name.trim().length === 0) {
    return 'Target table name is required.';
  }
  const change = args.change;
  if (!hasAnyField(change)) {
    return 'Set at least one option for the engine.';
  }
  if (engine === 'mssql') {
    if (hasPgsqlField(change) || hasMysqlField(change) || hasOracleField(change)) {
      return 'SQL Server only accepts mssql* fields (DATA_COMPRESSION / DURABILITY / SYSTEM_VERSIONING).';
    }
    if (
      change.mssqlSystemVersioning?.enabled === true
      && change.mssqlSystemVersioning.historyTable !== undefined
      && change.mssqlSystemVersioning.historyTable.trim().length === 0
    ) {
      return 'HISTORY_TABLE name cannot be blank.';
    }
    return null;
  }
  if (engine === 'postgres') {
    if (hasMssqlField(change) || hasMysqlField(change) || hasOracleField(change)) {
      return 'PostgreSQL only accepts pgsqlSetOptions / pgsqlResetOptions.';
    }
    const setEntries = change.pgsqlSetOptions ?? [];
    const resetEntries = change.pgsqlResetOptions ?? [];
    if (setEntries.length === 0 && resetEntries.length === 0) {
      return 'Provide at least one SET or RESET storage parameter.';
    }
    if (setEntries.some((entry) => entry.trim().length === 0)) {
      return 'SET entries cannot be blank — use the form `key = value`.';
    }
    if (resetEntries.some((entry) => entry.trim().length === 0)) {
      return 'RESET entries cannot be blank.';
    }
    return null;
  }
  if (engine === 'mysql') {
    if (hasMssqlField(change) || hasPgsqlField(change) || hasOracleField(change)) {
      return 'MySQL only accepts mysql* fields (ENGINE / CHARSET / COLLATE / ROW_FORMAT / KEY_BLOCK_SIZE / STATS_PERSISTENT).';
    }
    if (
      change.mysqlKeyBlockSize !== undefined
      && !isPositiveInteger(change.mysqlKeyBlockSize)
    ) {
      return 'KEY_BLOCK_SIZE must be a positive integer.';
    }
    return null;
  }
  // oracle
  if (hasMssqlField(change) || hasPgsqlField(change) || hasMysqlField(change)) {
    return 'Oracle only accepts oracle* fields (PCTFREE / PCTUSED / INITRANS / MAXTRANS / STORAGE / LOGGING / COMPRESS).';
  }
  for (const [label, value] of [
    ['PCTFREE', change.oraclePctfree],
    ['PCTUSED', change.oraclePctused],
    ['INITRANS', change.oracleInitrans],
    ['MAXTRANS', change.oracleMaxtrans],
  ] as const) {
    if (value !== undefined && !isPositiveInteger(value)) {
      return `${label} must be a positive integer.`;
    }
  }
  return null;
}
